import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MultiSelectButton } from '../../../../global/multi-select/multi-select-button';
import { IEventAttributeDetailsDTO } from '../../../../../api/attributes.dao';
import './event-attributes-multiselect.scss';
import Text from '../../../../global/text/text';
import DefaultButton from '../../../../global/default-button/default-button';
import { REMOVE_TEXT, addText, cancelText } from '../../../../../constants/common-labels.constants';

export type Props = {
    selectedEventAttributes: IEventAttributeDetailsDTO[];
    availableEventAttributes: IEventAttributeDetailsDTO[];
    setEventAttributes: (eventAttributes: IEventAttributeDetailsDTO[]) => void;
    removeEventAttribute: (eventAttribute: IEventAttributeDetailsDTO) => void;
};

type EventAttributeItem = {
    value: IEventAttributeDetailsDTO;
    eventAttributeName: string;
    eventAttributeId: number;
};

export const EventAttributesMultiselect = observer((props: Props): JSX.Element => {
    const intl = useIntl();

    const { setEventAttributes, removeEventAttribute, availableEventAttributes } = props;

    const eventAttributeList: EventAttributeItem[] = availableEventAttributes.map(attr => ({
        value: attr,
        eventAttributeName: attr.eventAttributeName,
        eventAttributeId: attr.eventAttributeId
    }));

    const eventAttributeToEventAttributeItem = (attr: IEventAttributeDetailsDTO): EventAttributeItem => ({
        value: attr,
        eventAttributeName: attr.eventAttributeName,
        eventAttributeId: attr.eventAttributeId
    });

    const selectedEventAttributes = props.selectedEventAttributes.map(eventAttributeToEventAttributeItem);
    const disabledEventAttributes = selectedEventAttributes;
    const onSetEventAttributes = (addedEventAttributes: EventAttributeItem[]): void =>
        setEventAttributes(addedEventAttributes.map(({ value }) => value));

    return (
        <section className='event-attributes-section m-bottom-20px'>
            <div className='event-attribute-list'>
                <Text
                    tag='div'
                    className='event-attribute-list__title'
                    message={intl.formatMessage({
                        id: 'oe.blueprints.event.eventAttributes',
                        defaultMessage: 'Event Attributes',
                        description: 'Label text'
                    })}
                />
                {selectedEventAttributes.map(({ value }) => {
                    return (
                        <div className='event-attribute-list-item' key={value.eventAttributeId}>
                            <Text
                                tag='div'
                                className='event-attribute-list-item__text'
                                message={value.eventAttributeName}
                                withEllipsis
                            />
                            <DefaultButton
                                text={<FormattedMessage {...REMOVE_TEXT} />}
                                modifier='remove'
                                onClick={() => removeEventAttribute(value)}
                            />
                        </div>
                    );
                })}
            </div>
            <MultiSelectButton
                items={eventAttributeList}
                disabled={disabledEventAttributes}
                value={selectedEventAttributes}
                keyProperty='eventAttributeId'
                nameProperty='eventAttributeName'
                text={<FormattedMessage
                    id='oe.eventAttribute.add'
                    description='Button text'
                    defaultMessage='{prefix} Event Attribute'
                    values={{ prefix: '+' }}
                />}
                applyButtonText={addText()}
                resetButtonText={cancelText()}
                onChange={onSetEventAttributes}
            />
        </section>
    );
});
